import { faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { FaStar, FaBolt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";

const PricingSection = () => {
  const navigate = useNavigate();
  const intl = useIntl();

  const pricingPlans = [
    {
      id: "trial",
      titleKey: "LANDINGPAGE.PRICING.TRIAL.TITLE",
      icon: <FaStar className="text-primary-1" />,
      priceKey: "LANDINGPAGE.PRICING.TRIAL.PRICE",
      requestsKey: "LANDINGPAGE.PRICING.TRIAL.REQUESTS",
      buttonTextKey: "LANDINGPAGE.PRICING.TRIAL.BUTTON",
      buttonStyle:
        "bg-transparent border border-[#464646] text-white hover:bg-[#9dffa033] cursor-pointer",
      featureKeys: [
        "LANDINGPAGE.PRICING.TRIAL.FEATURE1",
        "LANDINGPAGE.PRICING.TRIAL.FEATURE2",
        "LANDINGPAGE.PRICING.TRIAL.FEATURE3",
      ],
    },
    {
      id: "premium",
      titleKey: "LANDINGPAGE.PRICING.PREMIUM.TITLE",
      icon: <FaBolt className="text-primary-1" />,
      priceKey: "LANDINGPAGE.PRICING.PREMIUM.PRICE",
      requestsKey: "LANDINGPAGE.PRICING.PREMIUM.REQUESTS",
      buttonTextKey: "LANDINGPAGE.PRICING.PREMIUM.BUTTON",
      buttonStyle:
        "bg-transparent border border-[#464646] text-white hover:bg-[#9dffa033] cursor-pointer",
      featureKeys: [
        "LANDINGPAGE.PRICING.PREMIUM.FEATURE1",
        "LANDINGPAGE.PRICING.PREMIUM.FEATURE2",
        "LANDINGPAGE.PRICING.PREMIUM.FEATURE3",
        "LANDINGPAGE.PRICING.PREMIUM.FEATURE4",
        "LANDINGPAGE.PRICING.PREMIUM.FEATURE5",
      ],
    },
    {
      id: "professional",
      titleKey: "LANDINGPAGE.PRICING.PROFESSIONAL.TITLE",
      icon: <FaStar className="text-primary-1" />,
      priceKey: "LANDINGPAGE.PRICING.PROFESSIONAL.PRICE",
      requestsKey: "LANDINGPAGE.PRICING.PROFESSIONAL.REQUESTS",
      buttonTextKey: "LANDINGPAGE.PRICING.PROFESSIONAL.BUTTON",
      buttonStyle: "bg-[#50B553] text-white hover:bg-[#4AA642] cursor-pointer",
      isPopular: true,
      featureKeys: [
        "LANDINGPAGE.PRICING.PROFESSIONAL.FEATURE1",
        "LANDINGPAGE.PRICING.PROFESSIONAL.FEATURE2",
        "LANDINGPAGE.PRICING.PROFESSIONAL.FEATURE3",
        "LANDINGPAGE.PRICING.PROFESSIONAL.FEATURE4",
        "LANDINGPAGE.PRICING.PROFESSIONAL.FEATURE5",
        "LANDINGPAGE.PRICING.PROFESSIONAL.FEATURE6",
        "LANDINGPAGE.PRICING.PROFESSIONAL.FEATURE7",
      ],
    },
    {
      id: "enterprise",
      titleKey: "LANDINGPAGE.PRICING.ENTERPRISE.TITLE",
      icon: <FaStar className="text-primary-1" />,
      priceKey: "LANDINGPAGE.PRICING.ENTERPRISE.PRICE",
      requestsKey: "LANDINGPAGE.PRICING.ENTERPRISE.REQUESTS",
      buttonTextKey: "LANDINGPAGE.PRICING.ENTERPRISE.BUTTON",
      buttonStyle:
        "bg-transparent border border-[#464646] text-white hover:bg-[#9dffa033] cursor-pointer",
      isRecommended: true,
      featureKeys: [
        "LANDINGPAGE.PRICING.ENTERPRISE.FEATURE1",
        "LANDINGPAGE.PRICING.ENTERPRISE.FEATURE2",
        "LANDINGPAGE.PRICING.ENTERPRISE.FEATURE3",
        "LANDINGPAGE.PRICING.ENTERPRISE.FEATURE4",
        "LANDINGPAGE.PRICING.ENTERPRISE.FEATURE5",
        "LANDINGPAGE.PRICING.ENTERPRISE.FEATURE6",
        "LANDINGPAGE.PRICING.ENTERPRISE.FEATURE7",
        "LANDINGPAGE.PRICING.ENTERPRISE.FEATURE8",
      ],
    },
  ];

  const handleGetStarted = (planId) => {
    if (planId === "enterprise") {
      navigate("/contact");
    } else {
      navigate("/auth/signup");
    }
  };

  return (
    <div className="py-20 px-4 bg-[#071807]">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="pb-4 md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-br from-[#FFFFFF] from-[15.54%] to-[#5AE65E] to-[67.98%] md:text-[80px] text-[40px]">
            <FormattedMessage id="LANDINGPAGE.PRICING.TITLE" />
          </h1>
          <p className="text-[32px] text-white mb-2">
            <FormattedMessage id="LANDINGPAGE.PRICING.SUBTITLE" />
          </p>
          <p className="text-2xl font-light text-white">
            <FormattedMessage id="LANDINGPAGE.PRICING.VAT_NOTE" />
          </p>
        </div>

        {/* Pricing Cards */}

        <div className="w-full flex justify-center">
          <img
            src="/media/images/landing-page/bg-1.png"
            alt="bg-1"
            className="h-full w-full absolute l-0 hidden md:block"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pricingPlans.map((plan) => (
              <div
                key={plan.id}
                className={`relative rounded-3xl p-6 border text-white transition-all duration-300 hover:scale-105  border-[#0B4108EB] 
                  ${plan.isPopular ? `bg-gradient-to-br from-[#031602] from-[47.7%] to-[#50B553] to-[133.91%]` : `bg-[#031602]`}`}
              >
                {/* Popular/Recommended Badge */}
                {plan.isPopular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-[#5AE65E] text-black px-4 py-1 rounded-[11px] text-sm font-semibold">
                      <FormattedMessage id="LANDINGPAGE.PRICING.MOST_POPULAR" />
                    </span>
                  </div>
                )}
                {plan.isRecommended && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-[#5AE65E] text-black px-3 py-1 rounded-[11px] text-sm font-semibold whitespace-nowrap">
                      <FormattedMessage id="LANDINGPAGE.PRICING.RECOMMENDED" />
                    </span>
                  </div>
                )}

                {/* Plan Header */}
                <div className="mb-6">
                  <div className="flex items-center gap-3">
                    <div className="mb-2">{plan.icon}</div>
                    <h3 className="text-xl font-semibold text-white mb-2">
                      <FormattedMessage id={plan.titleKey} />
                    </h3>
                  </div>
                  <div className="flex items-center text-2xl font-bold text-primary-1 mb-1">
                    <FormattedMessage id={plan.priceKey} />
                  </div>
                  <div className=" flex gap-3 items-center text-sm text-gray-400">
                    <FaStar className="text-yellow-500" />
                    <FormattedMessage id={plan.requestsKey} />
                  </div>
                </div>

                {/* Button */}
                <button
                  onClick={() => handleGetStarted(plan.id)}
                  className={`w-full py-2 rounded-lg font-semibold transition-all duration-300 ${plan.buttonStyle}`}
                >
                  <FormattedMessage id={plan.buttonTextKey} />
                </button>

                {/* Features */}
                <div className="mt-6">
                  <h4 className="text-white font-semibold mb-3">
                    <FormattedMessage id="LANDINGPAGE.PRICING.PACKAGE_INCLUDES" />
                  </h4>
                  <ul className="space-y-2">
                    {plan.featureKeys.map((featureKey, index) => (
                      <li
                        key={index}
                        className="text-gray-300 text-sm flex items-center gap-2"
                      >
                        <FontAwesomeIcon icon={faCircleCheck} />
                        <FormattedMessage id={featureKey} />
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingSection;
